import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { useState } from "react";
import { Link } from "react-router-dom";

import { apiClient } from "~/api/ApiClient";
import { Icon } from "~/assets/icons/Icon";
import { UnstyledButton } from "~/components/mantine/button/Button";
import Card from "~/components/ui/card/Card";
import PendingStatus from "~/components/ui/pendingStatus/PendingStatus";
import ProgressBar from "~/components/ui/progressBar/ProgressBar";
import { useTeam } from "~/contexts/TeamProvider";
import { getTenantHostnames } from "~/helpers/tenantHostnames";
import { tryCatch } from "~/helpers/tryCatch";
import { useMarkOnboardingStep } from "~/hooks/useMarkOnboardingStep";
import { routes } from "~/router";

import { useCardDialog } from "../cardDialog/useCardDialog";
import { LAUNCH_CALL_URL } from "../consts";
import WelcomeVideo from "../welcome/WelcomeVideo";
import styles from "./LaunchChecklist.module.scss";

type Props = {
  hasSites: boolean;
  canCreateSite: boolean;
  // Offer to open the tour when it is not already on the page.
  canOpenTour?: boolean;
};

type ChecklistItem = {
  key: string;
  label: string;
  isDone: boolean;
  action?: React.ReactNode;
};

/**
 * Whether the agency's platform is live on its own domain: a hostname
 * whose DNS points to us. A team without a white-label account (or one the
 * backend refuses) has none.
 */
const useIsDomainConnected = (enabled: boolean) => {
  const { team } = useTeam();

  const { data } = useQuery({
    queryKey: ["autopto-domain-connected", team?.id],
    enabled: enabled && !!team,
    queryFn: async () => {
      const result = await tryCatch(apiClient.tenant.accountHostnamesList());

      if (result.error) return false;

      return getTenantHostnames(result.data.data).some(
        (hostname) => hostname.dns_set,
      );
    },
  });

  return Boolean(data);
};

const LaunchChecklist = ({ hasSites, canCreateSite, canOpenTour }: Props) => {
  const { team } = useTeam();
  const markOnboardingStep = useMarkOnboardingStep();
  const { canAddCard, openCardDialog, isOpeningCardDialog, isAwaitingCard } =
    useCardDialog();
  const [isTourOpen, setIsTourOpen] = useState(false);

  // A card launches the platform; its own domain waits for one.
  const isCardAdded = !team?.autopto_card_required;
  const isDomainConnected = useIsDomainConnected(isCardAdded);

  const cardAction = !canAddCard ? (
    <span className={styles.hint}>Ask your team admin to add a card</span>
  ) : isAwaitingCard ? (
    <PendingStatus>Activating your platform…</PendingStatus>
  ) : (
    <UnstyledButton
      className={styles.action}
      onClick={openCardDialog}
      disabled={isOpeningCardDialog}
    >
      Launch
    </UnstyledButton>
  );

  const items: ChecklistItem[] = [
    {
      // The platform step precedes the dashboard, so it's always done here.
      key: "platform",
      label: "Your branded platform is ready",
      isDone: true,
    },
    {
      key: "tour",
      label: "Watch the 2-minute tour",
      isDone: Boolean(team?.onboarding_tour_watched_at),
      action: canOpenTour && !isTourOpen && (
        <UnstyledButton
          className={styles.action}
          onClick={() => setIsTourOpen(true)}
        >
          Watch
        </UnstyledButton>
      ),
    },
    {
      key: "site",
      label: "Add your first website",
      isDone: hasSites,
      action: canCreateSite && (
        <Link
          to={routes.site.wizard({ tab: "new" })}
          className={styles.action}
          onClick={() => localStorage.removeItem("siteId")}
        >
          Add
        </Link>
      ),
    },
    {
      key: "launch-platform",
      label: "Launch Your Platform",
      isDone: isCardAdded,
      action: cardAction,
    },
    {
      key: "launch-call",
      label: "Book your Launch Call",
      isDone: Boolean(team?.onboarding_launch_call_at),
      action: (
        <a
          href={LAUNCH_CALL_URL}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.action}
          onClick={() => markOnboardingStep("onboarding_launch_call_at")}
        >
          Book
        </a>
      ),
    },
    {
      key: "domain",
      label: "Launch Platform on my Brand's Domain",
      isDone: isDomainConnected,
      action: isCardAdded ? (
        <Link
          to={routes.settings.tab({ tabId: "white-label" })}
          className={styles.action}
        >
          Connect
        </Link>
      ) : (
        canAddCard && (
          <UnstyledButton
            className={styles.action}
            onClick={openCardDialog}
            disabled={isOpeningCardDialog || isAwaitingCard}
          >
            Connect
          </UnstyledButton>
        )
      ),
    },
  ];

  const doneCount = items.filter((item) => item.isDone).length;
  const currentKey = items.find((item) => !item.isDone)?.key;

  return (
    <div className={styles.wrapper}>
      {isTourOpen && <WelcomeVideo />}
      <Card title="Launch Checklist">
        <ProgressBar
          min={doneCount}
          max={items.length}
          customText={`${doneCount} of ${items.length} done`}
        />
        <ol className={styles.list}>
          {items.map((item) => (
            <li
              key={item.key}
              className={clsx(styles.item, {
                [styles.done]: item.isDone,
                [styles.current]: item.key === currentKey,
              })}
            >
              {item.isDone ? (
                <Icon
                  iconName="GreenCheckmarkIcon"
                  width="1.5rem"
                  height="1.5rem"
                  className={styles.doneMarker}
                />
              ) : (
                <span className={styles.marker} />
              )}
              <span className={styles.label}>{item.label}</span>
              {!item.isDone && item.action}
            </li>
          ))}
        </ol>
      </Card>
    </div>
  );
};

export default LaunchChecklist;
